import { format, isValid, parse } from "date-fns";

import { type AppLocale, DEFAULT_LOCALE, toIntlLocale } from "@/i18n/locale";

const MonthIndexMap: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

const DEFAULT_TIME_ZONE = "UTC";

const DueDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const SidebarDueDateFormatOptions: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const SidebarDueDateFormatter = new Intl.DateTimeFormat("en-US", SidebarDueDateFormatOptions);

const SpanishSidebarDateFormatOptions: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

const SpanishSidebarDateFormatter = new Intl.DateTimeFormat(
  toIntlLocale("es"),
  SpanishSidebarDateFormatOptions
);

const UploadDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

const SpanishTimeFormatOptions: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

const SpanishTimeFormatter = new Intl.DateTimeFormat(toIntlLocale("es"), SpanishTimeFormatOptions);

const EnglishTimeFormatOptions: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
};

const EnglishTimeFormatter = new Intl.DateTimeFormat("en-US", EnglishTimeFormatOptions);

export function formatDueDate(date: Date, locale: AppLocale = DEFAULT_LOCALE): string {
  if (Number.isNaN(date.getTime())) return "";

  if (locale === "pt") {
    return `${formatCompactDateValue(date)} - ${formatCompactTimeValue(date)}`;
  }

  return formatLegacyDueDate(date);
}

function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatIsoDate(value: string | null | undefined): string {
  if (!value) return "";

  const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) return dateMatch[1];

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

const ISO_DATE_PATTERN = "yyyy-MM-dd";

const NumericDateFormatOptions: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

export function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

  const date = parse(value, ISO_DATE_PATTERN, new Date());
  return isValid(date) ? date : null;
}

export function toIsoDate(date: Date): string {
  return format(date, ISO_DATE_PATTERN);
}

export function formatNumericDate(
  value: string | null | undefined,
  locale: AppLocale = DEFAULT_LOCALE
): string {
  const date = parseIsoDate(value);
  if (!date) return "";

  return new Intl.DateTimeFormat(toIntlLocale(locale), NumericDateFormatOptions).format(date);
}

export function formatSidebarDueDate(
  value: string | null | undefined,
  locale: AppLocale = DEFAULT_LOCALE,
  timeZone = DEFAULT_TIME_ZONE
): string {
  const date = parseSidebarDate(value);
  if (!date) return "";

  return formatSidebarDateValue(date, locale, resolveFormattingTimeZone(value, timeZone));
}

export function formatUploadDate(
  value: string | null | undefined,
  locale: AppLocale = DEFAULT_LOCALE
): string {
  const date = parseApiDate(value);
  if (!date) return "";

  if (locale === "pt") return formatCompactDateValue(date);
  if (locale === "es") return formatSidebarDateValue(date, locale);

  return UploadDateFormatter.format(date);
}

export interface SidebarLastUpdateOptions {
  meridiem?: "lower" | "upper";
}

export function formatSidebarLastUpdate(
  value: string | null | undefined,
  locale: AppLocale = DEFAULT_LOCALE,
  timeZone = DEFAULT_TIME_ZONE,
  { meridiem = "lower" }: SidebarLastUpdateOptions = {}
): string {
  const date = parseSidebarDate(value);
  if (!date) return "";

  const formattingTimeZone = resolveFormattingTimeZone(value, timeZone);

  if (locale === "pt") {
    return `${formatCompactDateValue(date, formattingTimeZone)} - ${formatCompactTimeValue(date, formattingTimeZone)}`;
  }

  const englishTime = () => {
    const time = formatDateValue("en-US", EnglishTimeFormatOptions, date, formattingTimeZone);
    return meridiem === "upper" ? time.toLocaleUpperCase("en-US") : time.toLocaleLowerCase("en-US");
  };

  const timeLabel =
    locale === "en"
      ? englishTime()
      : formatDateValue(toIntlLocale("es"), SpanishTimeFormatOptions, date, formattingTimeZone);

  return `${formatSidebarDateValue(date, locale, formattingTimeZone)} - ${timeLabel}`;
}

export function formatCompactDateTimeParts(
  value: string | null | undefined,
  locale: AppLocale = DEFAULT_LOCALE
): {
  dateLabel: string;
  timeLabel: string;
} {
  const date = parseApiDate(value);
  if (!date) return { dateLabel: "", timeLabel: "" };

  if (locale !== "pt") {
    const timeLabel =
      locale === "en"
        ? EnglishTimeFormatter.format(date).toLocaleLowerCase("en-US")
        : SpanishTimeFormatter.format(date);

    return {
      dateLabel: formatSidebarDateValue(date, locale),
      timeLabel,
    };
  }

  return {
    dateLabel: formatCompactDateValue(date),
    timeLabel: formatCompactTimeValue(date),
  };
}

export function formatDateTimeParts(
  value: string | null | undefined,
  locale: AppLocale = DEFAULT_LOCALE
): {
  dateLabel: string;
  timeLabel: string;
} {
  return formatCompactDateTimeParts(value, locale);
}

export function formatCompactDate(
  value: string | null | undefined,
  locale: AppLocale = DEFAULT_LOCALE,
  timeZone?: string
): string {
  if (!timeZone) {
    const localDate = parseApiDate(value);
    if (!localDate) return "";

    return locale === "pt"
      ? formatCompactDateValue(localDate)
      : SidebarDueDateFormatter.format(localDate);
  }

  const date = parseSidebarDate(value);
  if (!date) return "";

  const formattingTimeZone = resolveFormattingTimeZone(value, timeZone);

  return locale === "pt"
    ? formatCompactDateValue(date, formattingTimeZone)
    : formatDateValue("en-US", SidebarDueDateFormatOptions, date, formattingTimeZone);
}

function formatLegacyDueDate(date: Date): string {
  const formattedParts = DueDateFormatter.formatToParts(date).reduce<Record<string, string>>(
    (parts, part) => {
      if (part.type !== "literal") {
        parts[part.type] = part.value;
      }

      return parts;
    },
    {}
  );

  return `${formattedParts.month} ${formattedParts.day}, ${formattedParts.year} - ${formattedParts.hour}:${formattedParts.minute} ${formattedParts.dayPeriod.toLowerCase()}`;
}

function formatCompactDateValue(date: Date, timeZone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  if (timeZone) {
    options.timeZone = resolveTimeZone(timeZone);
  }

  const parts = new Intl.DateTimeFormat(toIntlLocale("pt"), options)
    .formatToParts(date)
    .filter(({ type }) => type === "day" || type === "month" || type === "year")
    .map(({ type, value }) => {
      if (type !== "month") return value;

      const month = value.replaceAll(".", "").slice(0, 3);
      return `${month.charAt(0).toUpperCase()}${month.slice(1)}`;
    });

  return parts.join(" ");
}

function formatSidebarDateValue(date: Date, locale: AppLocale, timeZone?: string): string {
  if (locale === "pt") return formatCompactDateValue(date, timeZone);
  if (locale === "es") {
    return timeZone
      ? formatDateValue(toIntlLocale("es"), SpanishSidebarDateFormatOptions, date, timeZone)
      : SpanishSidebarDateFormatter.format(date);
  }

  return timeZone
    ? formatDateValue("en-US", SidebarDueDateFormatOptions, date, timeZone)
    : SidebarDueDateFormatter.format(date);
}

function formatCompactTimeValue(date: Date, timeZone?: string): string {
  const intlLocale = toIntlLocale("pt");
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  };

  const formatter = new Intl.DateTimeFormat(intlLocale, options);
  const value = timeZone
    ? formatDateValue(intlLocale, options, date, timeZone)
    : formatter.format(date);

  return value.toLocaleLowerCase(intlLocale);
}

function parseSidebarDate(value: string | null | undefined): Date | null {
  const dateOnlyMatch = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }

  return parseApiDate(value);
}

function resolveFormattingTimeZone(value: string | null | undefined, timeZone: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value ?? "") ? DEFAULT_TIME_ZONE : timeZone;
}

function formatDateValue(
  locale: string,
  options: Intl.DateTimeFormatOptions,
  date: Date,
  timeZone: string
): string {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: DEFAULT_TIME_ZONE }).format(
      date
    );
  }
}

function resolveTimeZone(timeZone: string): string {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

export function parseDueDate(value: string): number {
  const match = value.match(
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2}), (\d{4}) - (\d{1,2}):(\d{2}) (am|pm)$/i
  );

  if (!match) {
    return Date.parse(value);
  }

  const [, monthLabel, day, year, hour, minute, period] = match;
  const monthIndex = MonthIndexMap[monthLabel];

  if (monthIndex === undefined) return Number.NaN;

  const normalizedHour =
    period.toLowerCase() === "pm" ? (Number(hour) % 12) + 12 : Number(hour) % 12;

  return new Date(Number(year), monthIndex, Number(day), normalizedHour, Number(minute)).getTime();
}
